class CarouselController {
  constructor(element, options = {}) {
    this.carousel = element;
    this.options = {
      transitionSpeed: 300,
      snapThreshold: 0.3,
      velocityThreshold: 0.5,
      resistance: 0.5,
      ...options
    };

    this.track = this.carousel.querySelector('.carousel-track');
    this.slides = this.carousel.querySelectorAll('.carousel-slide');
    this.prevButton = this.carousel.querySelector('.carousel-button--prev');
    this.nextButton = this.carousel.querySelector('.carousel-button--next');

    if (!this.validateSetup()) return;

    this.state = {
      currentIndex: 0,
      isDragging: false,
      startX: 0,
      currentTranslateX: 0,
      prevTranslateX: 0,
      dragStartTime: 0,
      lastDragX: 0,
      velocity: 0,
      animationFrame: null
    };

    this.init();
  }

  validateSetup() {
    if (!this.track || !this.slides.length) {
      console.error('Required carousel elements not found');
      return false;
    }
    return true;
  }

  init() {
    this.setupStyles();
    this.bindEvents();
    this.setupResizeObserver();
    this.updateCarousel(false);
  }

  setupStyles() {
    this.track.style.touchAction = 'pan-y pinch-zoom';
    this.track.style.userSelect = 'none';
    this.track.style.willChange = 'transform';
  }

  bindEvents() {
    // Touch events
    this.track.addEventListener('touchstart', this.handleDragStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.handleDragMove.bind(this), { passive: false });
    this.track.addEventListener('touchend', this.handleDragEnd.bind(this));
    this.track.addEventListener('touchcancel', this.handleDragEnd.bind(this));

    // Mouse events
    this.track.addEventListener('mousedown', this.handleDragStart.bind(this));
    window.addEventListener('mousemove', this.handleDragMove.bind(this));
    window.addEventListener('mouseup', this.handleDragEnd.bind(this));
    window.addEventListener('mouseleave', this.handleDragEnd.bind(this));

    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    // Prevent click during drag
    this.track.addEventListener('click', (e) => {
      if (this.state.isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true });
  }

  setupResizeObserver() {
    const resizeObserver = new ResizeObserver(_.debounce(() => {
      this.updateCarousel(false);
    }, 250));
    resizeObserver.observe(this.carousel);
  }

  handleDragStart(e) {
    if (e.button === 2) return; // Ignore right clicks

    this.state.isDragging = true;
    this.state.startX = this.getEventX(e);
    this.state.dragStartTime = Date.now();
    this.state.lastDragX = this.state.startX;
    this.state.velocity = 0;

    this.track.style.transition = 'none';
    this.track.classList.add('is-dragging');
    cancelAnimationFrame(this.state.animationFrame);
  }

  handleDragMove(e) {
    if (!this.state.isDragging) return;
    e.preventDefault();

    const currentX = this.getEventX(e);
    const deltaX = currentX - this.state.lastDragX;
    const timeDelta = Date.now() - this.state.dragStartTime;

    // Calculate velocity (pixels per millisecond)
    this.state.velocity = deltaX / (timeDelta || 16.67);

    // Apply resistance at edges
    const maxTranslate = this.getMaxTranslate();
    const proposedTranslate = this.state.prevTranslateX + (currentX - this.state.startX);
    
    if (proposedTranslate > 0 || proposedTranslate < maxTranslate) {
      this.state.currentTranslateX = proposedTranslate * this.options.resistance;
    } else {
      this.state.currentTranslateX = proposedTranslate;
    }

    this.state.lastDragX = currentX;
    this.setTransform(this.state.currentTranslateX);
  }

  handleDragEnd() {
    if (!this.state.isDragging) return;
    this.state.isDragging = false;
    this.track.classList.remove('is-dragging');

    const dragDistance = this.state.currentTranslateX - this.state.prevTranslateX;
    const dragTime = Date.now() - this.state.dragStartTime;
    const velocity = Math.abs(this.state.velocity);

    let snapIndex = this.state.currentIndex;

    // Determine direction based on velocity or distance
    if (velocity > this.options.velocityThreshold || 
        Math.abs(dragDistance) > this.getSlideWidth() * this.options.snapThreshold) {
      snapIndex += dragDistance < 0 ? 1 : -1;
    }

    this.goToSlide(snapIndex);
  }

  getEventX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
  }

  getSlideWidth() {
    return this.slides[0].offsetWidth + 
           parseInt(getComputedStyle(this.carousel).getPropertyValue('--slide-gap') || 0);
  }

  getMaxTranslate() {
    const slidesPerView = parseInt(getComputedStyle(this.carousel)
      .getPropertyValue('--slides-per-view') || 1);
    return -((this.slides.length - slidesPerView) * this.getSlideWidth());
  }

  setTransform(x) {
    this.track.style.transform = `translateX(${x}px)`;
  }

  goToSlide(index) {
    const maxIndex = this.slides.length - 1;
    this.state.currentIndex = Math.max(0, Math.min(index, maxIndex));
    
    const slideWidth = this.getSlideWidth();
    this.state.currentTranslateX = -(this.state.currentIndex * slideWidth);
    this.state.prevTranslateX = this.state.currentTranslateX;

    this.track.style.transition = `transform ${this.options.transitionSpeed}ms ease-out`;
    this.setTransform(this.state.currentTranslateX);
    this.updateButtons();
  }

  prev() {
    if (this.state.currentIndex > 0) {
      this.goToSlide(this.state.currentIndex - 1);
    }
  }

  next() {
    const maxIndex = this.slides.length - 1;
    if (this.state.currentIndex < maxIndex) {
      this.goToSlide(this.state.currentIndex + 1);
    }
  }

  updateButtons() {
    if (this.prevButton) {
      this.prevButton.disabled = this.state.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = 
        this.state.currentIndex >= this.slides.length - 1;
    }
  }

  updateCarousel(animate = true) {
    if (!animate) {
      this.track.style.transition = 'none';
    }
    this.goToSlide(this.state.currentIndex);
  }
}

// Helper function to initialize carousels
function initializeCarousels() {
  const carouselSelectors = [
    '[data-new-products-carousel]',
    '[data-collection-carousel]',
    '[data-related-products]'
  ];

  carouselSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(carousel => {
      // Map class names to match the controller's expected structure
      const trackClass = carousel.querySelector('.new-products-carousel__track, .collection-carousel__track, .related-products__track');
      const slideClass = carousel.querySelectorAll('.new-products-carousel__slide, .collection-carousel__slide, .related-products__slide');
      const prevButton = carousel.querySelector('[class*="button--prev"]');
      const nextButton = carousel.querySelector('[class*="button--next"]');

      if (trackClass) trackClass.classList.add('carousel-track');
      slideClass.forEach(slide => slide.classList.add('carousel-slide'));

      new CarouselController(carousel, {
        transitionSpeed: parseInt(getComputedStyle(carousel)
          .getPropertyValue('--transition-speed')) || 300
      });
    });
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCarousels);
} else {
  initializeCarousels();
}